import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';

interface Tool {
  id: string;
  agentDid: string;
  name: string;
  description?: string;
  version: string;
  capabilities: string[];
  inputSchema: Record<string, unknown>;
  policyId?: string | null;
  rowVersion?: number;
  createdAt: string;
  updatedAt: string;
  revokedAt?: string | null;
  revokedReason?: string | null;
  lastInvokedAt?: string | null;
}

interface IBCTPolicy {
  id: string;
  name: string;
  ownerDid: string;
}

interface ToolFormState {
  name: string;
  description: string;
  version: string;
  capabilitiesText: string;
  inputSchemaText: string;
  policyId: string;
}

const emptyForm: ToolFormState = {
  name: '',
  description: '',
  version: '1.0.0',
  capabilitiesText: '',
  inputSchemaText: JSON.stringify(
    {
      $schema: 'https://json-schema.org/draft/2020-12/schema',
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: false,
    },
    null,
    2,
  ),
  policyId: '',
};

interface ListEnvelope<T> {
  items?: T[];
  data?: T[];
}

function extract<T>(envelope: ListEnvelope<T> | T[] | null | undefined): T[] {
  if (!envelope) return [];
  if (Array.isArray(envelope)) return envelope;
  if (Array.isArray(envelope.items)) return envelope.items;
  if (Array.isArray(envelope.data)) return envelope.data;
  return [];
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export default function AgentToolsPage() {
  const { did } = useParams<{ did: string }>();
  const { t } = useTranslation(['agents', 'common']);
  const [tools, setTools] = useState<Tool[]>([]);
  const [policies, setPolicies] = useState<IBCTPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);

  // Modal state
  const [modalKind, setModalKind] = useState<'register' | 'edit' | 'revoke' | null>(null);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [form, setForm] = useState<ToolFormState>(emptyForm);
  const [revokeReason, setRevokeReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const refresh = useCallback(async () => {
    if (!did) return;
    setLoading(true);
    setError(null);
    try {
      const tq = includeRevoked ? '?includeRevoked=true' : '';
      const [toolsRaw, policiesRaw] = await Promise.all([
        apiFetch<ListEnvelope<Tool>>(`/agents/${encodePathSegment(did)}/tools${tq}`),
        apiFetch<ListEnvelope<IBCTPolicy>>(`/operators/me/policies`).catch(() => ({ items: [] })),
      ]);
      setTools(extract<Tool>(toolsRaw));
      setPolicies(extract<IBCTPolicy>(policiesRaw));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common:errors.unknown'));
    } finally {
      setLoading(false);
    }
  }, [did, includeRevoked, t]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const handle = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(handle);
  }, [toast]);

  const policyById = useMemo(() => {
    const map = new Map<string, IBCTPolicy>();
    for (const p of policies) map.set(p.id, p);
    return map;
  }, [policies]);

  const openRegister = () => {
    setEditingTool(null);
    setForm(emptyForm);
    setFormError(null);
    setModalKind('register');
  };

  const openEdit = (tool: Tool) => {
    setEditingTool(tool);
    setForm({
      name: tool.name,
      description: tool.description ?? '',
      version: tool.version,
      capabilitiesText: tool.capabilities.join(', '),
      inputSchemaText: JSON.stringify(tool.inputSchema ?? {}, null, 2),
      policyId: tool.policyId ?? '',
    });
    setFormError(null);
    setModalKind('edit');
  };

  const openRevoke = (tool: Tool) => {
    setEditingTool(tool);
    setRevokeReason('');
    setFormError(null);
    setModalKind('revoke');
  };

  const closeModal = () => {
    setModalKind(null);
    setEditingTool(null);
    setForm(emptyForm);
    setRevokeReason('');
    setFormError(null);
  };

  const formatSchema = () => {
    try {
      const parsed = JSON.parse(form.inputSchemaText) as unknown;
      setForm((f) => ({ ...f, inputSchemaText: JSON.stringify(parsed, null, 2) }));
      setFormError(null);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('tools.errors.schemaParse'));
    }
  };

  const handleSubmit = async () => {
    if (!did) return;
    setSubmitting(true);
    setFormError(null);

    try {
      let inputSchema: Record<string, unknown>;
      try {
        inputSchema = JSON.parse(form.inputSchemaText) as Record<string, unknown>;
      } catch (e) {
        throw new Error(e instanceof Error ? e.message : t('tools.errors.schemaParse'));
      }
      const capabilities = form.capabilitiesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const body: Record<string, unknown> = {
        description: form.description || undefined,
        version: form.version,
        capabilities,
        inputSchema,
        policyId: form.policyId || null,
      };

      if (modalKind === 'register') {
        body.name = form.name;
        await apiFetch(`/agents/${encodePathSegment(did)}/tools`, {
          method: 'POST',
          headers: { 'Idempotency-Key': uuid() },
          body: JSON.stringify(body),
        });
        setToast({ kind: 'success', msg: t('tools.toasts.registerSuccess') });
      } else if (modalKind === 'edit' && editingTool) {
        body.ifVersion = editingTool.rowVersion ?? 1;
        await apiFetch(
          `/agents/${encodePathSegment(did)}/tools/${encodePathSegment(editingTool.id)}`,
          { method: 'PUT', body: JSON.stringify(body) },
        );
        setToast({ kind: 'success', msg: t('tools.toasts.updateSuccess') });
      }
      closeModal();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('tools.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!did || !editingTool) return;
    if (!revokeReason.trim()) {
      setFormError(t('tools.errors.revokeReasonRequired'));
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await apiFetch(
        `/agents/${encodePathSegment(did)}/tools/${encodePathSegment(editingTool.id)}/revoke`,
        { method: 'POST', body: JSON.stringify({ reason: revokeReason.trim() }) },
      );
      setToast({ kind: 'success', msg: t('tools.toasts.revokeSuccess') });
      closeModal();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('tools.errors.revoke'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!did) {
    return <div className="text-center py-20 text-red-500">{t('common:errors.invalidParams')}</div>;
  }

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-xs text-text-faint">
        <Link to="/ai-agents" className="hover:text-primary">{t('detail.breadcrumbBase')}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <Link to={`/ai-agents/${did}`} className="hover:text-primary">{did}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <span className="font-semibold text-text-main">{t('tools.title')}</span>
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-text-main">{t('tools.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('tools.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-bold text-text-muted">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
              className="rounded"
            />
            {t('tools.includeRevoked')}
          </label>
          <button onClick={openRegister} className="btn-primary text-xs">
            <span className="material-symbols-outlined mr-1" style={{ fontSize: 14 }}>add</span>
            {t('tools.register')}
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`rounded-lg border px-4 py-2 text-xs font-semibold ${
            toast.kind === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle bg-slate-50/50">
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('tools.table.name')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('tools.table.capabilities')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('tools.table.policy')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('tools.table.status')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('tools.table.lastInvoked')}</th>
              <th className="w-32 px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-text-faint">{t('common:loading')}</td></tr>
            ) : error ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-red-500">{error}</td></tr>
            ) : tools.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-text-faint">{t('tools.empty')}</td></tr>
            ) : tools.map((tool) => {
              const status = tool.revokedAt ? 'revoked' : 'active';
              return (
                <tr key={tool.id} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <div className="flex flex-col">
                      <span className="font-mono text-xs font-bold text-text-main">{tool.name}</span>
                      <span className="text-[10px] text-text-faint">v{tool.version}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {tool.capabilities.map((c) => (
                        <span key={c} className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {tool.policyId ? (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                        {policyById.get(tool.policyId)?.name ?? tool.policyId}
                      </span>
                    ) : (
                      <span className="text-[11px] text-text-faint">{t('tools.noPolicy')}</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={status === 'revoked' ? 'error' : 'success'}>
                      {status === 'revoked' ? t('tools.statusRevoked') : t('tools.statusActive')}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs text-text-muted">
                      {tool.lastInvokedAt ? new Date(tool.lastInvokedAt).toLocaleString() : '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {!tool.revokedAt && (
                        <>
                          <button
                            onClick={() => openEdit(tool)}
                            className="rounded-lg p-1 text-text-faint hover:bg-slate-100 hover:text-text-main"
                            title={t('tools.edit')}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                          </button>
                          <button
                            onClick={() => openRevoke(tool)}
                            className="rounded-lg p-1 text-text-faint hover:bg-red-50 hover:text-red-600"
                            title={t('tools.revoke')}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>block</span>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Register / Edit modal */}
      <Modal
        open={modalKind === 'register' || modalKind === 'edit'}
        onClose={closeModal}
        wide
        title={modalKind === 'edit' ? t('tools.modal.editTitle') : t('tools.modal.registerTitle')}
      >
        <div className="space-y-4">
          {modalKind === 'register' && (
            <FormField
              label={t('tools.fields.name')}
              hint={t('tools.fields.nameHint')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="weather.lookup"
            />
          )}
          <FormField
            label={t('tools.fields.description')}
            as="textarea"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label={t('tools.fields.version')}
              value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
              placeholder="1.0.0"
            />
            <FormField
              label={t('tools.fields.policy')}
              as="select"
              value={form.policyId}
              onChange={(e) => setForm({ ...form, policyId: e.target.value })}
            >
              <option value="">{t('tools.noPolicy')}</option>
              {policies.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </FormField>
          </div>
          <FormField
            label={t('tools.fields.capabilities')}
            hint={t('tools.fields.capabilitiesHint')}
            value={form.capabilitiesText}
            onChange={(e) => setForm({ ...form, capabilitiesText: e.target.value })}
            placeholder="tool:weather.lookup, tool:weather.forecast"
          />
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-xs font-bold text-text-muted">{t('tools.fields.inputSchema')}</label>
              <button
                type="button"
                onClick={formatSchema}
                className="text-[10px] font-bold text-primary hover:underline"
              >
                {t('tools.fields.format')}
              </button>
            </div>
            <textarea
              className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 font-mono text-xs"
              rows={10}
              value={form.inputSchemaText}
              onChange={(e) => setForm({ ...form, inputSchemaText: e.target.value })}
            />
            <p className="mt-1 text-[10px] text-text-faint">{t('tools.fields.inputSchemaHint')}</p>
          </div>
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
            <button onClick={closeModal} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary text-xs disabled:opacity-50"
            >
              {submitting ? t('common:saving') : modalKind === 'edit' ? t('common:save') : t('tools.modal.registerCta')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Revoke confirm modal */}
      <Modal
        open={modalKind === 'revoke'}
        onClose={closeModal}
        title={t('tools.modal.revokeTitle')}
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            {t('tools.modal.revokeBody', { name: editingTool?.name ?? '' })}
          </p>
          <FormField
            label={t('tools.fields.revokeReason')}
            as="textarea"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder={t('tools.fields.revokeReasonPlaceholder')}
          />
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
            <button onClick={closeModal} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button
              onClick={handleRevoke}
              disabled={submitting}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? t('common:saving') : t('tools.revoke')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
